import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Book, ArrowLeft, ExternalLink } from 'lucide-react';

interface Guide {
  slug: string;
  title: string;
  description: string;
  category: string;
  content: string;
}

const GuidesPage: React.FC = () => {
  const { slug } = useParams<{ slug?: string }>();

  const guides: Guide[] = [
    {
      slug: 'async-data-fetching',
      title: 'Async Data Fetching',
      description: 'Learn how to fetch countries and cities from external APIs',
      category: 'Advanced',
      content: `# Async Data Fetching Guide

The library provides powerful support for loading address data (countries, cities, districts) from external APIs while maintaining fallback support for built-in data.

## Basic Setup

Use the \`createFetcherAdapter\` helper to configure your data fetchers:

\`\`\`typescript
import { useAddressForm } from 'salla-address-manager/react';
import { createFetcherAdapter } from 'salla-address-manager/helpers';

function MyForm() {
  const form = useAddressForm({
    locale: 'en',
    ...createFetcherAdapter({
      countryFetcher: async () => {
        const response = await fetch('/api/countries');
        return response.json();
      },
      cityFetcher: async (countryId) => {
        const response = await fetch(\`/api/countries/\${countryId}/cities\`);
        return response.json();
      },
      districtFetcher: async (cityId) => {
        const response = await fetch(\`/api/cities/\${cityId}/districts\`);
        return response.json();
      },
      responseTransform: {
        wrapper: 'data.data',      // Extract data from nested response
        localizedName: true        // Use localized name field
      }
    }, 'en')
  });

  // Access loading states
  if (form.loading.countries) {
    return <div>Loading countries...</div>;
  }

  return <form>{/* Your form fields */}</form>;
}
\`\`\`

## Response Format Handling

The adapter automatically handles different response formats:

### Wrapped Response
\`\`\`json
{
  "status": "success",
  "data": {
    "data": [
      { "id": "1", "name": "Country 1" }
    ]
  }
}
\`\`\`

### Direct Array
\`\`\`json
[
  { "id": "1", "name": "Country 1" }
]
\`\`\`

## Loading States

Access loading states for each data type:

\`\`\`typescript
const form = useAddressForm({ /* config */ });

console.log(form.loading.countries);  // boolean
console.log(form.loading.cities);     // boolean
console.log(form.loading.districts);  // boolean
\`\`\`

## Error Handling

The library gracefully falls back to built-in data when API calls fail:

\`\`\`typescript
const form = useAddressForm({
  ...createFetcherAdapter({
    countryFetcher: async () => {
      try {
        const res = await fetch('/api/countries');
        if (!res.ok) throw new Error('Failed to fetch');
        return res.json();
      } catch (error) {
        console.error('Error fetching countries:', error);
        // Library automatically uses built-in countries
        throw error;
      }
    }
  }, 'en')
});
\`\`\`

## Caching

Data is automatically cached to avoid unnecessary API calls:

\`\`\`typescript
// First call - fetches from API
const cities1 = await form.getCitiesForCountry('SA');

// Second call - uses cached data
const cities2 = await form.getCitiesForCountry('SA');
\`\`\`

## Best Practices

1. **Always handle errors** - Network requests can fail
2. **Show loading states** - Provide feedback to users
3. **Use response transforms** - Normalize your API responses
4. **Cache aggressively** - Reduce unnecessary API calls
5. **Test offline** - Ensure fallback data works
`,
    },
    {
      slug: 'national-address-integration',
      title: 'Saudi National Address Integration',
      description: 'Integrate Saudi Arabia national address system with autocomplete',
      category: 'Integration',
      content: `# Saudi National Address Integration

Integrate with Saudi Arabia's national address system for enhanced address input.

## Setup

Use the \`useNationalAddressForm\` hook for React:

\`\`\`typescript
import { useNationalAddressForm } from 'salla-address-manager/react';

function NationalAddressForm() {
  const form = useNationalAddressForm({
    locale: 'ar',
    enableNationalAddress: true,
    onNationalAddressSelect: (address) => {
      console.log('Selected national address:', address);
      // Auto-fill form with national address data
    }
  });

  return (
    <form onSubmit={form.handleSubmit}>
      <div>
        <label>Short Address (National Address)</label>
        <input
          {...form.getFieldProps('nationalAddress')}
          placeholder="AAAA1234"
        />
      </div>

      <div>
        <label>Building Number</label>
        <input {...form.getFieldProps('buildingNumber')} />
      </div>

      <div>
        <label>Postal Code</label>
        <input {...form.getFieldProps('postalCode')} />
      </div>

      {/* Other address fields */}
    </form>
  );
}
\`\`\`

## National Address Fields

When national address is enabled, additional fields become available:

- \`nationalAddress\` - Short address code (e.g., AAAA1234)
- \`buildingNumber\` - Building number (رقم المبنى)
- \`postalCode\` - Postal code (الرمز البريدي)
- \`additionalNumber\` - Additional number (الرقم الإضافي)

## Validation

National address fields have specific validation rules:

\`\`\`typescript
{
  nationalAddress: '8228',           // 4 digits
  buildingNumber: '1234',            // 4 digits
  postalCode: '12345',               // 5 digits
  additionalNumber: '1234'           // 4 digits
}
\`\`\`

## Autocomplete Integration

Implement autocomplete using the national address API:

\`\`\`typescript
import { useState, useEffect } from 'react';

function NationalAddressAutocomplete() {
  const [suggestions, setSuggestions] = useState([]);
  const form = useNationalAddressForm({
    locale: 'ar',
    enableNationalAddress: true
  });

  useEffect(() => {
    const fetchSuggestions = async () => {
      if (form.values.nationalAddress?.length >= 4) {
        const res = await fetch(
          \`/api/national-address/search?q=\${form.values.nationalAddress}\`
        );
        const data = await res.json();
        setSuggestions(data);
      }
    };

    const debounce = setTimeout(fetchSuggestions, 300);
    return () => clearTimeout(debounce);
  }, [form.values.nationalAddress]);

  return (
    <div>
      <input {...form.getFieldProps('nationalAddress')} />
      
      {suggestions.length > 0 && (
        <ul className="suggestions">
          {suggestions.map((suggestion) => (
            <li
              key={suggestion.id}
              onClick={() => form.setNationalAddress(suggestion)}
            >
              {suggestion.displayText}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
\`\`\`

## Best Practices

1. **Always validate** - National address fields have specific formats
2. **Provide clear labels** - Use Arabic labels for Arabic locale
3. **Show format examples** - Help users understand the expected format
4. **Implement autocomplete** - Make it easier for users to find addresses
`,
    },
    {
      slug: 'multi-language-support',
      title: 'Multi-Language Support',
      description: 'Implement Arabic and English support with RTL handling',
      category: 'Internationalization',
      content: `# Multi-Language Support

The library provides built-in support for English and Arabic with automatic RTL handling.

## Basic Usage

Simply set the \`locale\` option:

\`\`\`typescript
import { useAddressForm } from 'salla-address-manager/react';

// English
const formEN = useAddressForm({ locale: 'en' });

// Arabic
const formAR = useAddressForm({ locale: 'ar' });
\`\`\`

## Dynamic Language Switching

Allow users to switch languages dynamically:

\`\`\`typescript
import { useState } from 'react';
import { useAddressForm } from 'salla-address-manager/react';

function MultiLanguageForm() {
  const [locale, setLocale] = useState<'en' | 'ar'>('en');
  
  const form = useAddressForm({ locale });

  return (
    <div dir={locale === 'ar' ? 'rtl' : 'ltr'}>
      <div className="language-switcher">
        <button
          onClick={() => setLocale('en')}
          className={locale === 'en' ? 'active' : ''}
        >
          English
        </button>
        <button
          onClick={() => setLocale('ar')}
          className={locale === 'ar' ? 'active' : ''}
        >
          العربية
        </button>
      </div>

      <form onSubmit={form.handleSubmit}>
        {/* Form fields */}
      </form>
    </div>
  );
}
\`\`\`

## RTL Styling

Apply RTL-specific styles when Arabic is selected:

\`\`\`css
/* Default LTR */
.form-field {
  text-align: left;
}

/* RTL for Arabic */
[dir="rtl"] .form-field {
  text-align: right;
}

[dir="rtl"] .form-field input {
  text-align: right;
}
\`\`\`

## Localized Validation Messages

All validation messages are automatically localized:

\`\`\`typescript
// English locale
form.errors.street // "Street is required"

// Arabic locale
form.errors.street // "الشارع مطلوب"
\`\`\`

## Custom Translations

Override default translations if needed:

\`\`\`typescript
import { setApplicationLocale } from 'salla-address-manager';

setApplicationLocale('ar');
\`\`\`

## Best Practices

1. **Always set dir attribute** - Use \`dir="rtl"\` for Arabic
2. **Test both directions** - Ensure UI works in LTR and RTL
3. **Use logical properties** - Use \`margin-inline-start\` instead of \`margin-left\`
4. **Localize all text** - Don't hardcode strings in components
`,
    },
    {
      slug: 'form-validation',
      title: 'Form Validation',
      description: 'Configure validation rules and handle errors',
      category: 'Validation',
      content: `# Form Validation Guide

The library provides comprehensive validation with country-specific rules.

## Validation Configuration

Configure when validation occurs:

\`\`\`typescript
import { useAddressForm } from 'salla-address-manager/react';

const form = useAddressForm({
  locale: 'en',
  validation: {
    validateOnChange: true,    // Validate as user types
    validateOnBlur: true,      // Validate when field loses focus
    debounceMs: 300           // Debounce validation (ms)
  }
});
\`\`\`

## Accessing Errors

Error messages are available in the \`errors\` object:

\`\`\`typescript
const form = useAddressForm({ locale: 'en' });

// Check if field has error
if (form.errors.street) {
  console.log(form.errors.street); // "Street is required"
}

// Check overall form validity
if (!form.isValid) {
  console.log('Form has errors');
}
\`\`\`

## Displaying Errors

Show validation errors in your UI:

\`\`\`tsx
<div className="form-field">
  <label htmlFor="street">Street</label>
  <input
    id="street"
    {...form.getFieldProps('street')}
    className={form.errors.street ? 'error' : ''}
  />
  {form.errors.street && (
    <span className="error-message">{form.errors.street}</span>
  )}
</div>
\`\`\`

## Country-Specific Rules

Validation rules automatically adapt to the selected country:

### Saudi Arabia
- Postal code: Optional, 5 digits
- Building number: 4 digits (with national address)
- District: Required

### United States
- ZIP code: Required, 5 digits
- State: Required (2-letter code)

### United Arab Emirates
- Emirate: Required
- Area: Required

## Manual Validation

Trigger validation manually:

\`\`\`typescript
const form = useAddressForm({ locale: 'en' });

// Validate entire form
const isValid = await form.validateForm();

// Validate single field
const streetError = await form.validateField('street');
\`\`\`

## Custom Validation

Add custom validation logic:

\`\`\`typescript
const form = useAddressForm({
  locale: 'en',
  onValidate: (values) => {
    const errors: any = {};
    
    // Custom validation
    if (values.street && values.street.includes('P.O. Box')) {
      errors.street = 'P.O. Box addresses are not allowed';
    }
    
    return errors;
  }
});
\`\`\`

## Validation Events

Listen to validation events:

\`\`\`typescript
form.on('validation:error', (event) => {
  console.log('Validation failed:', event.errors);
});

form.on('validation:success', () => {
  console.log('Validation passed!');
});
\`\`\`
`,
    },
    {
      slug: 'styling-guide',
      title: 'Styling Guide',
      description: 'Learn how to style the address form with any CSS framework',
      category: 'Customization',
      content: `# Styling Guide

Since the library is headless, you have complete control over styling.

## Using Tailwind CSS

\`\`\`tsx
import { useAddressForm } from 'salla-address-manager/react';

function StyledForm() {
  const form = useAddressForm({ locale: 'en' });

  return (
    <form onSubmit={form.handleSubmit} className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Country
        </label>
        <select
          {...form.getFieldProps('country')}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        >
          {form.countries.map(country => (
            <option key={country.id} value={country.id}>
              {country.name}
            </option>
          ))}
        </select>
        {form.errors.country && (
          <p className="mt-1 text-sm text-red-600">{form.errors.country}</p>
        )}
      </div>

      <button
        type="submit"
        disabled={!form.isValid}
        className="w-full bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
      >
        {form.isSubmitting ? 'Saving...' : 'Save Address'}
      </button>
    </form>
  );
}
\`\`\`

## Using Material-UI

\`\`\`tsx
import { useAddressForm } from 'salla-address-manager/react';
import {
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel
} from '@mui/material';

function MUIForm() {
  const form = useAddressForm({ locale: 'en' });

  return (
    <form onSubmit={form.handleSubmit}>
      <FormControl fullWidth margin="normal">
        <InputLabel>Country</InputLabel>
        <Select {...form.getFieldProps('country')}>
          {form.countries.map(country => (
            <MenuItem key={country.id} value={country.id}>
              {country.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <TextField
        fullWidth
        margin="normal"
        label="Street"
        {...form.getFieldProps('street')}
        error={!!form.errors.street}
        helperText={form.errors.street}
      />

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!form.isValid}
      >
        Save Address
      </Button>
    </form>
  );
}
\`\`\`

## CSS Modules

\`\`\`tsx
import styles from './AddressForm.module.css';
import { useAddressForm } from 'salla-address-manager/react';

function ModularForm() {
  const form = useAddressForm({ locale: 'en' });

  return (
    <form onSubmit={form.handleSubmit} className={styles.form}>
      <div className={styles.field}>
        <label className={styles.label}>Street</label>
        <input
          {...form.getFieldProps('street')}
          className={styles.input}
        />
        {form.errors.street && (
          <span className={styles.error}>{form.errors.street}</span>
        )}
      </div>
    </form>
  );
}
\`\`\`

## Styled Components

\`\`\`tsx
import styled from 'styled-components';
import { useAddressForm } from 'salla-address-manager/react';

const Form = styled.form\`
  max-width: 600px;
  margin: 0 auto;
\`;

const Field = styled.div\`
  margin-bottom: 1rem;
\`;

const Label = styled.label\`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 500;
\`;

const Input = styled.input\`
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ccc;
  border-radius: 4px;
\`;

function StyledComponentForm() {
  const form = useAddressForm({ locale: 'en' });

  return (
    <Form onSubmit={form.handleSubmit}>
      <Field>
        <Label>Street</Label>
        <Input {...form.getFieldProps('street')} />
      </Field>
    </Form>
  );
}
\`\`\`
`,
    },
  ];

  const categories = Array.from(new Set(guides.map((g) => g.category)));

  const selectedGuide = slug
    ? guides.find((g) => g.slug === slug)
    : null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="container-custom py-12">
          {selectedGuide ? (
            <>
              <Link
                to="/guides"
                className="inline-flex items-center space-x-2 text-salla-primary hover:underline mb-4"
              >
                <ArrowLeft className="w-4 h-4" />
                <span>Back to Guides</span>
              </Link>
              <h1 className="text-4xl font-bold mb-2">{selectedGuide.title}</h1>
              <p className="text-xl text-gray-600">{selectedGuide.description}</p>
            </>
          ) : (
            <>
              <h1 className="text-4xl font-bold mb-4">Guides</h1>
              <p className="text-xl text-gray-600 max-w-3xl">
                In-depth guides for specific features and use cases.
              </p>
            </>
          )}
        </div>
      </div>

      <div className="container-custom section">
        {selectedGuide ? (
          /* Guide Content */
          <div className="max-w-4xl mx-auto">
            <div className="card prose prose-lg max-w-none">
              <div className="whitespace-pre-wrap">{selectedGuide.content}</div>
            </div>
          </div>
        ) : (
          /* Guides List */
          <>
            {categories.map((category) => (
              <div key={category} className="mb-12">
                <h2 className="text-2xl font-bold mb-6">{category}</h2>
                <div className="grid md:grid-cols-2 gap-6">
                  {guides
                    .filter((g) => g.category === category)
                    .map((guide) => (
                      <Link
                        key={guide.slug}
                        to={`/guides/${guide.slug}`}
                        className="card hover:shadow-md transition-shadow group"
                      >
                        <div className="flex items-start justify-between mb-3">
                          <Book className="w-8 h-8 text-salla-primary flex-shrink-0" />
                          <ExternalLink className="w-4 h-4 text-gray-400 group-hover:text-salla-primary transition-colors" />
                        </div>
                        <h3 className="font-bold text-xl mb-2 group-hover:text-salla-primary transition-colors">
                          {guide.title}
                        </h3>
                        <p className="text-gray-600">{guide.description}</p>
                      </Link>
                    ))}
                </div>
              </div>
            ))}
          </>
        )}
      </div>
    </div>
  );
};

export default GuidesPage;

